import { useMemo } from "react";

import {
  AgeBandedGenderRate,
  AgeBandedRate,
  AgeBandedTieredRate,
  AgeBandedTobaccoRate,
  BasicLifeRate,
  CompositeRate,
  CriticalIllnessRate,
  FlatRate,
  PlanRates,
  VolLifeRate,
} from "../../constants/aquafina";
import CollapsableSection from "../sections/collapsable-section";
import { MessageRow, NotProvidedRow } from "../table/rows";
import { RATE_TYPES } from "../../constants/rateTypes";

import CompositeRates from "./composite-rates";
import FlatRates from "./flat-rates";
import AgeBandedRates from "./age-banded-rates";
import AgeBandedGenderRates from "./age-banded-gender-rates";
import AgeBandedTobaccoRates from "./age-banded-tobacco-rates";
import BasicLifeRates from "./basic-life-rates";
import VoluntaryLifeRates from "./voluntary-life-rates";
import CriticalIllnessRates from "./critical-illness-rates";
import AgeBandedTieredRates from "./age-banded-tiered-rates";

const Rates = ({
  rates,
  inconsistentRateData,
  totalPlans,
  cellColSpan = 1,
}: {
  rates: PlanRates[];
  inconsistentRateData: boolean;
  totalPlans: number;
  cellColSpan?: number;
}) => (
  <CollapsableSection numberOfColumns={totalPlans * cellColSpan} title="Rates">
    <RatesData
      rates={rates}
      inconsistentRateData={inconsistentRateData}
      totalPlans={totalPlans}
      cellColSpan={cellColSpan}
    />
  </CollapsableSection>
);

const RatesData = ({
  rates,
  inconsistentRateData,
  totalPlans,
  cellColSpan,
}: {
  rates: PlanRates[];
  inconsistentRateData: boolean;
  totalPlans: number;
  cellColSpan: number;
}) => {
  const { hasSameType, firstType } = useMemo(() => {
    if (rates.length === 0 || rates.every((r) => r === null)) {
      return { hasSameType: false, firstType: null };
    }
    const firstType = rates[0]?.type;
    const hasSameType = rates.every((rate) => rate?.type === firstType);

    return { hasSameType, firstType };
  }, [rates]);

  if (inconsistentRateData) {
    return (
      <MessageRow
        colSpan={totalPlans * cellColSpan}
        message="This section cannot be displayed due to inconsistent data"
      />
    );
  }

  if (rates.length === 0) {
    return (
      <MessageRow
        colSpan={totalPlans * cellColSpan}
        message="No data to show"
      />
    );
  }

  if (rates.every((r) => r === null)) {
    return <NotProvidedRow colSpan={totalPlans * cellColSpan} />;
  }

  if (!hasSameType) {
    return (
      <MessageRow
        colSpan={totalPlans * cellColSpan}
        message="Different types of rates"
      />
    );
  }

  return (
    <>
      {firstType === RATE_TYPES.COMPOSITE && (
        <CompositeRates rates={rates as CompositeRate[]} />
      )}
      {firstType === RATE_TYPES.FLAT && (
        <FlatRates rates={rates as FlatRate[]} />
      )}
      {firstType === RATE_TYPES.AGE_BANDED && (
        <AgeBandedRates rates={rates as AgeBandedRate[]} />
      )}
      {firstType === RATE_TYPES.AGE_BANDED_GENDER && (
        <AgeBandedGenderRates rates={rates as AgeBandedGenderRate[]} />
      )}
      {firstType === RATE_TYPES.AGE_BANDED_TOBACCO && (
        <AgeBandedTobaccoRates rates={rates as AgeBandedTobaccoRate[]} />
      )}
      {firstType === RATE_TYPES.VOLUNTARY_LIFE && (
        <VoluntaryLifeRates rates={rates as VolLifeRate[]} />
      )}
      {firstType === RATE_TYPES.BASIC_LIFE && (
        <BasicLifeRates rates={rates as BasicLifeRate[]} />
      )}
      {firstType === RATE_TYPES.CRITICAL_ILLNESS && (
        <CriticalIllnessRates rates={rates as CriticalIllnessRate[]} />
      )}
      {firstType === RATE_TYPES.AGE_BANDED_TIERED && (
        <AgeBandedTieredRates rates={rates as AgeBandedTieredRate[]} />
      )}
    </>
  );
};

export default Rates;
